import React, { createContext, useContext, useEffect, useState } from "react";

export type CartLine = { product_id: string; title: string; price: number; kind: string; image_url?: string; reader_id: string; reader_name?: string; quantity: number; variant?: string | null; shipping_fee?: number };

type CartCtx = {
  lines: CartLine[]; add: (l: CartLine) => string | null; remove: (id: string, variant?: string | null) => void;
  setQty: (id: string, variant: string | null | undefined, q: number) => void; clear: () => void;
  subtotal: number; shipping: number; count: number;
};

const Ctx = createContext<CartCtx>(null as any);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try { return JSON.parse(localStorage.getItem("tt_cart") || "[]"); } catch { return []; }
  });
  useEffect(() => { localStorage.setItem("tt_cart", JSON.stringify(lines)); }, [lines]);

  const add = (l: CartLine) => {
    if (lines.length && lines[0].reader_id !== l.reader_id) return "Your cart holds items from another shop. Checkout or clear it first.";
    setLines((prev) => {
      const i = prev.findIndex((x) => x.product_id === l.product_id && (x.variant || null) === (l.variant || null));
      if (i >= 0) { const c = [...prev]; c[i] = { ...c[i], quantity: c[i].quantity + l.quantity }; return c; }
      return [...prev, l];
    });
    return null;
  };
  const remove = (id: string, variant?: string | null) => setLines((p) => p.filter((x) => !(x.product_id === id && (x.variant || null) === (variant || null))));
  const setQty = (id: string, variant: string | null | undefined, q: number) =>
    setLines((p) => p.map((x) => x.product_id === id && (x.variant || null) === (variant || null) ? { ...x, quantity: Math.max(1, q) } : x));
  const clear = () => setLines([]);
  const subtotal = lines.reduce((s, l) => s + l.price * l.quantity, 0);
  const shipping = lines.reduce((s, l) => s + (l.kind === "physical" ? (l.shipping_fee || 0) : 0), 0);
  const count = lines.reduce((s, l) => s + l.quantity, 0);
  return <Ctx.Provider value={{ lines, add, remove, setQty, clear, subtotal, shipping, count }}>{children}</Ctx.Provider>;
}

export const useCart = () => useContext(Ctx);
